import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UsersThree } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useCheckoutMode } from "@/context/CheckoutModeContext";
import { isPartnerHost } from "@/lib/partnerHost";
import { cn } from "@/lib/utils";
import { forgetGroupMembership, useActiveGroups } from "./useGroupMembership";

// Loaded on the first open only: most pages never need the chat.
const GroupChatWindow = lazy(() => import("./GroupChatWindow"));

const SIZE = 56;
const EDGE = 12;
const TOP_MIN = 80; // under the site header
const ABOVE_BARS = 160; // first shown clear of a page's sticky booking bar
const DRAG_FROM = 6; // a press that moves less than this is a tap
const STORE = "toukky_group_bubble";
// From this width the bubble opens the chat in a small window beside it;
// under it (a phone) the chat takes the whole screen.
const DESK = "(min-width: 768px)";

const clamp = (n, low, high) => Math.min(Math.max(n, low), Math.max(low, high));
const viewport = () => ({ w: window.innerWidth || 0, h: window.innerHeight || 0 });

function savedPlace() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE) || "null");
    if (saved && ["left", "right"].includes(saved.side) && Number.isFinite(saved.top)) return saved;
  } catch { /* no storage here: the first place */ }
  return null;
}

function savePlace(place) {
  try { localStorage.setItem(STORE, JSON.stringify(place)); } catch { /* kept for this visit only */ }
}

function useDesk() {
  const [desk, setDesk] = useState(() => typeof window !== "undefined" && Boolean(window.matchMedia?.(DESK).matches));
  useEffect(() => {
    const query = window.matchMedia?.(DESK);
    if (!query) return undefined;
    const onChange = () => setDesk(query.matches);
    onChange();
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);
  return desk;
}

/** Where the bubble rests: on one side's edge, at a height kept inside the window. */
function resting(place) {
  const { w, h } = viewport();
  const side = place?.side || "right";
  const top = clamp(Number.isFinite(place?.top) ? place.top : h - SIZE - ABOVE_BARS, TOP_MIN, h - SIZE - EDGE);
  return { side, top, left: side === "left" ? EDGE : w - SIZE - EDGE };
}

/**
 * A member's group, floating on the screen (owner, 2026-10-03): a round
 * bubble on every page that opens the group's conversation (the list of
 * groups when there are several) over the page the traveller is on, never
 * instead of it (owner, 2026-10-04: "ça doit te envoyer sur la page là où
 * vous étiez"): on a computer in a small window beside it, which moves by
 * its title bar; on a phone over the whole screen, closed by its cross or
 * the phone's back button, back on the same page at the same place. It can
 * be moved with a finger or the mouse and settles on the nearer edge, where
 * it stays from page to page on this device. A red count says how many
 * messages from the others wait unread. Nothing for a guest or a
 * traveller with no active group; none inside the groups themselves, during
 * a checkout or on the partner site.
 */
export default function GroupBubble() {
  const { user } = useAuth() || {};
  const { t } = useSettings();
  const { isCheckout } = useCheckoutMode();
  const location = useLocation();
  // A new page asks again once the last answer is old: a group just made
  // shows. Every 30 seconds too, so a new message shows on the bubble.
  // A message read in the window asks again at once, so the red count follows.
  const [readTick, setReadTick] = useState(0);
  const groups = useActiveGroups(user?.user_id, `${location.pathname}#${readTick}`, { pollMs: 30000 });
  const desk = useDesk();
  const [open, setOpen] = useState(false);
  const link = useRef(null);
  const navigate = useNavigate();
  // On a phone the open chat is a step in the history, so the back button
  // closes it and leaves the page as it was.
  const screenOpen = !desk && Boolean(location.state?.groupChat);
  const origin = `${location.pathname}${location.search}`;
  const [place, setPlace] = useState(() => resting(savedPlace()));
  const [moving, setMoving] = useState(null);
  const press = useRef(null);
  const swallowClick = useRef(false);

  // Back on its edge when the window changes size (a phone turned, a desk resized).
  useEffect(() => {
    const onResize = () => setPlace((current) => resting(current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // A booking's page is a checkout until it is paid (the header's own rule):
  // nothing there may pull the traveller away from a half-finished payment.
  const checkout = isCheckout && /^\/booking\/[^/]+$/.test(location.pathname);
  const hidden = !user || groups.length === 0 || checkout || isPartnerHost() || /^\/groups(\/|$)/.test(location.pathname);
  // The window goes with the bubble, and on a phone-sized window.
  useEffect(() => { if (hidden || !desk) setOpen(false); }, [hidden, desk]);
  const close = useCallback(({ focus = true } = {}) => {
    if (desk) setOpen(false); else navigate(-1);
    if (focus) link.current?.focus();
  }, [desk, navigate]);
  // To the group's page or the search: on a phone in place of the open chat,
  // so the back button then returns to the page the bubble was tapped on.
  const leave = useCallback((path, state) => {
    if (desk) { setOpen(false); navigate(path, { state }); } else navigate(path, { replace: true, state });
  }, [desk, navigate]);
  const read = useCallback(() => { forgetGroupMembership(); setReadTick((n) => n + 1); }, []);
  if (hidden) return null;

  const one = groups.length === 1;
  const target = one ? `/groups/${encodeURIComponent(groups[0].id)}` : "/groups";
  // The group messages from the others not yet read, all groups together.
  const unread = groups.reduce((sum, group) => sum + (Number.isInteger(group.unread) && group.unread > 0 ? group.unread : 0), 0);
  const label = `${one ? `${t("nav.groupTrips")}, ${groups[0].title}` : `${t("nav.groupTrips")} (${groups.length})`}${unread ? `, ${unread} ${t("nav.unreadShort")}` : ""}`;

  const onPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    press.current = { id: event.pointerId, x: event.clientX, y: event.clientY, left: place.left, top: place.top, last: null };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event) => {
    const p = press.current;
    if (!p || p.id !== event.pointerId) return;
    const dx = event.clientX - p.x;
    const dy = event.clientY - p.y;
    if (!p.last && Math.hypot(dx, dy) < DRAG_FROM) return;
    const { w, h } = viewport();
    p.last = { left: clamp(p.left + dx, 0, w - SIZE), top: clamp(p.top + dy, TOP_MIN, h - SIZE - EDGE) };
    setMoving(p.last);
  };
  const onPointerEnd = (event) => {
    const p = press.current;
    if (!p || p.id !== event.pointerId) return;
    press.current = null;
    if (!p.last) return;
    // Laid down: it settles on the nearer edge and stays there.
    swallowClick.current = true;
    const settled = resting({ side: p.last.left + SIZE / 2 < viewport().w / 2 ? "left" : "right", top: p.last.top });
    setMoving(null);
    setPlace(settled);
    savePlace({ side: settled.side, top: settled.top });
  };
  // A move is not a tap. A tap opens the chat over this page: on a computer
  // it opens (or closes) the window; on a phone, the whole-screen chat.
  const onClick = (event) => {
    if (swallowClick.current) {
      swallowClick.current = false;
      event.preventDefault();
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    if (desk) setOpen((current) => !current);
    else if (!screenOpen) navigate(`${origin}${location.hash}`, { state: { ...(location.state || {}), groupChat: true } });
  };

  const at = moving || place;
  return (<>
    <Link ref={link} to={target} aria-label={label} title={label} draggable={false} data-testid="group-bubble" data-side={place.side}
      aria-haspopup="dialog" aria-expanded={desk ? open : screenOpen} aria-controls={(desk ? open : screenOpen) ? "group-chat-window" : undefined}
      onClick={onClick} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}
      style={{ left: at.left, top: at.top }}
      className={cn("fixed z-sticky inline-flex size-14 touch-none select-none items-center justify-center rounded-full bg-touki-night text-white shadow-tk-pop animate-in fade-in zoom-in-90 duration-base ease-tk-out motion-reduce:animate-none",
        moving ? "scale-105 cursor-grabbing" : "transition-[left,top,background-color] duration-base ease-tk-out hover:bg-touki-night-dark motion-reduce:transition-none")}>
      <UsersThree size={26} weight="bold" aria-hidden="true" />
      {unread > 0 && (
        <span aria-hidden="true" data-testid="group-bubble-unread"
          className="absolute -end-1 -top-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-touki-red px-1.5 text-caption font-bold tabular-nums text-white ring-2 ring-white animate-in zoom-in-50 duration-base ease-tk-out motion-reduce:animate-none">
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
    {(desk ? open : screenOpen) && <Suspense fallback={null}>
      <GroupChatWindow mode={desk ? "window" : "screen"} groups={groups} userId={user.user_id} bubble={{ ...place, size: SIZE }}
        origin={origin} onClose={close} onLeave={leave} onRead={read} />
    </Suspense>}
  </>);
}
